import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Container,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { LogOut, GetUserRoles, SwitchRole } from "../../Redux/Actions/authAction";
import { respondToRequests } from "../../Redux/Actions/userStatusService";
import { useUser } from "../../Context/UserContext";
import { usePermissions } from "../../Context/PermissionContext";

function AccountsComponent() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userRole, clearUserInfo, updateUserInfo } = useUser();
  const { updatePermissions } = usePermissions();
  const authState = useSelector((state) => state.user);
  const userInfo = authState?.userInfo || {};
  
  const [activeTab, setActiveTab] = useState(0);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);

  // Set initial tab based on navigation source
  useEffect(() => {
    if (location.state?.from === 'switchAccount') {
      setActiveTab(0); // All Accounts tab
    } else if (location.state?.from === 'requests') {
      setActiveTab(1); // Pending Requests tab
    }
  }, [location.state]);

  // Call GetUserRoles API on component mount
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        setApiLoading(true);
        const response = await dispatch(GetUserRoles());
        
        if (response.payload?.statusCode === 200) {
          const rolesData = response.payload.data || [];
          setUserRoles(rolesData);
          
          // Process the roles data
          processUserRoles(rolesData);
        } else {
          console.error("Failed to fetch user roles:", response);
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
      } finally {
        setApiLoading(false);
      }
    };

    fetchUserRoles();
  }, [dispatch]);

  // Process user roles to extract accounts and requests
  const processUserRoles = (rolesData) => {
    const managerAccounts = [];
    const acceptedSubManagerAccounts = [];
    const pendingRequests = [];

    rolesData.forEach(role => {
      if (role.role === 'MANAGER') {
        // Manager account
        managerAccounts.push({
          id: role.id,
          name: role.entityInfo?.name || 'Manager Account',
          email: role.user?.email || 'manager@prime.com',
          role: 'MANAGER',
          type: 'Manager Account',
          entityInfo: role.entityInfo,
          userRoleId: role.id,
          parentUserRoleId: null,
          accountingFirmAssignmentId: null
        });
      } else if (role.role === 'SUB_MANAGER') {
        // Check if sub-manager has parents with different statuses
        if (role.parents && role.parents.length > 0) {
          role.parents.forEach(parent => {
            if (parent.status === 'ACCEPTED') {
              // Get group name from allowed permissions
              const groupName = parent.allowedPermissions?.[0]?.permissionGroup?.groupName || 'Unknown Group';
              
              // Accepted sub-manager account
              acceptedSubManagerAccounts.push({
                id: `${role.id}-${parent.id}`,
                name: parent.parentUserRole?.entityInfo?.name || 'Unknown Entity',
                email: role.user?.email || 'unknown@email.com',
                role: 'SUB_MANAGER',
                type: 'Sub-Manager Account',
                parentRole: parent.parentUserRole?.role || 'Unknown Role',
                parentEntityName: parent.parentUserRole?.entityInfo?.name || 'Unknown Entity',
                parentEmail: parent.parentUserRole?.user?.email || 'unknown@email.com',
                entityInfo: role.entityInfo,
                parentInfo: parent.parentUserRole,
                allowedPermissions: parent.allowedPermissions,
                groupName: groupName,
                userRoleId: role.id,
                parentUserRoleId: parent.parentUserRole?.id,
                accountingFirmAssignmentId: parent.accountingFirmAssignmentId || null
              });
            } else if (parent.status === 'PENDING') {
              // Pending request
              pendingRequests.push({
                id: parent.id,
                entityName: parent.parentUserRole?.entityInfo?.name || 'Unknown Entity',
                parentRole: parent.parentUserRole?.role || 'Unknown Role',
                status: parent.status,
                parentUserRole: parent.parentUserRole,
                createdAt: parent.createdAt,
                subManagerInfo: role.entityInfo
              });
            }
          });
        }
      }
    });

    // Set the accounts data
    setAccounts([...managerAccounts, ...acceptedSubManagerAccounts]);
    
    // Set the requests data
    setRequests(pendingRequests);
    
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAccept = async (requestId) => {
    setLoading(true);
    try {
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'ACCEPTED' }
            : req
        )
      );
    } catch (error) {
      console.error("Error accepting request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async (requestId) => {
    setLoading(true);
    try {
      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: 'DENIED' }
            : req
        )
      );
    } catch (error) {
      console.error("Error declining request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (activeTab === 0) {
      // Switch account logic
      if (selectedAccount) {
        setLoading(true);
        try {
          // Find the selected account
          const selectedAccountData = accounts.find(acc => acc.id === selectedAccount);
          
          if (selectedAccountData) {
            // Prepare switch role request body
            const switchRequestBody = {
              userRoleId: selectedAccountData.userRoleId,
              parentUserRoleId: selectedAccountData.parentUserRoleId,
              accountingFirmAssignmentId: selectedAccountData.accountingFirmAssignmentId,
            };

            // Call switch role API using Redux action
            const switchResponse = await dispatch(SwitchRole(switchRequestBody));

            if (switchResponse.payload?.statusCode === 200 || switchResponse.payload?.statusCode === 201) {
              // Update tokens in localStorage with new tokens from response
              if (switchResponse.payload?.data?.accessToken) {
                localStorage.setItem('accessToken', switchResponse.payload.data.accessToken);
              }
              if (switchResponse.payload?.data?.refreshToken) {
                localStorage.setItem('refreshToken', switchResponse.payload.data.refreshToken);
              }

              // Update Redux store with new tokens (similar to login)
              dispatch({
                type: "user/setAuthDetails",
                payload: { 
                  token: switchResponse.payload.data.accessToken, 
                  refreshToken: switchResponse.payload.data.refreshToken 
                },
              });

              // Store account information
              localStorage.setItem("type", selectedAccountData.role);
              
              // If it's a SUB_MANAGER account, store permissions and parent info
              if (selectedAccountData.role === 'SUB_MANAGER' && selectedAccountData.allowedPermissions) {
                const permissions = selectedAccountData.allowedPermissions[0]?.permissionGroup?.apiNames || [];
                updatePermissions(permissions, selectedAccountData.role);
                
                // Store parent's entity info for SUB_MANAGER
                if (selectedAccountData.parentInfo?.entityInfo) {
                  localStorage.setItem("parentEntityName", selectedAccountData.parentInfo.entityInfo.name);
                  localStorage.setItem("parentEntityType", selectedAccountData.parentInfo.entityInfo.entityType);
                }
              }

              // Update user info in context with new tokens
              const loginResponse = {
                data: {
                  accessToken: switchResponse.payload?.data?.accessToken || userInfo.accessToken,
                  refreshToken: switchResponse.payload?.data?.refreshToken || userInfo.refreshToken,
                  userRoles: userRoles || []
                }
              };
              updateUserInfo(loginResponse);
              
              navigate("/dashboard");
            } else {
              console.error("Failed to switch role:", switchResponse);
            }
          }
        } catch (error) {
          console.error("Error switching account:", error);
        } finally {
          setLoading(false);
        }
      }
    } else {
      // Pending requests logic
      setLoading(true);
      try {
        const markedRequests = requests.filter(req => req.status === 'ACCEPTED' || req.status === 'DENIED');
        
        if (markedRequests.length === 0) {
          console.error("No requests marked for submission");
          return;
        }

        const requestBody = markedRequests.map(req => ({
          relationId: req.id,
          status: req.status
        }));

        const response = await dispatch(respondToRequests(requestBody));
        
        if (response?.payload?.statusCode === 200) {
          if (markedRequests.length === 1) {
            navigate("/dashboard");
          } else {
            dispatch(LogOut());
            navigate("/login");
          }
        } else {
          console.error("Failed to respond to requests:", response);
        }
      } catch (error) {
        console.error("Error submitting requests:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const pendingRequests = requests.filter(req => req.status === 'PENDING');
  const markedRequests = requests.filter(req => req.status === 'ACCEPTED' || req.status === 'DENIED');

  const managerAccounts = accounts.filter(acc => acc.role === 'MANAGER');
  const subManagerAccounts = accounts.filter(acc => acc.role === 'SUB_MANAGER');

  // Auto-select the first account if only one available
  useEffect(() => {
    if (accounts.length === 1) {
      setSelectedAccount(accounts[0].id);
    }
  }, [accounts]);

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 1, 
            fontSize: '2rem', 
            fontWeight: 700,
            color: '#333'
          }}
        >
          Accounts
        </Typography>
        
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666', 
            fontSize: '0.9rem',
            lineHeight: 1.4
          }}
        >
          Lorem ipsum dolor sit amet consectetur. Accumsan eget est.
        </Typography>
      </Box>

      {/* Main Content Card */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          minHeight: '60vh',
          backgroundColor: 'white',
          borderRadius: '12px',
          width: '40%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                minHeight: 48,
                color: '#666',
                '&.Mui-selected': {
                  color: '#1976d2',
                },
              },
            }}
          >
            <Tab label="All Accounts" />
            <Tab label="Pending Requests" />
          </Tabs>
        </Box>

        {/* Loading State */}
        {apiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: '#666' }}>
              Loading accounts...
            </Typography>
          </Box>
        )}

        {/* Tab Content */}
        {!apiLoading && (
          <>
            {/* All Accounts Tab */}
            {activeTab === 0 && (
              <Box>
                {/* Manager Account */}
                {managerAccounts.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
                      Your Manager Account
                    </Typography>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
                        {managerAccounts.map((account) => (
                          <Box 
                            key={account.id}
                            sx={{ 
                              p: 2, 
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              mb: 2,
                              backgroundColor: selectedAccount === account.id ? '#e3f2fd' : 'white',
                              '&:hover': {
                                backgroundColor: selectedAccount === account.id ? '#e3f2fd' : '#f5f5f5'
                              }
                            }}
                          >
                            <FormControlLabel
                              value={account.id}
                              control={<Radio />}
                              label={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                      {account.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                                      {account.email}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              sx={{ width: '100%', margin: 0 }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </>
                )}

                {/* Sub-Manager Accounts */}
                {subManagerAccounts.length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333', mt: 3 }}>
                      Your Sub-Manager Accounts
                    </Typography>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
                        {subManagerAccounts.map((account) => (
                          <Box 
                            key={account.id}
                            sx={{ 
                              p: 2, 
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              mb: 2,
                              backgroundColor: selectedAccount === account.id ? '#e3f2fd' : 'white',
                              '&:hover': {
                                backgroundColor: selectedAccount === account.id ? '#e3f2fd' : '#f5f5f5'
                              }
                            }}
                          >
                            <FormControlLabel
                              value={account.id}
                              control={<Radio />}
                              label={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                                      {account.parentEntityName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                                      {account.parentEmail}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem', fontWeight: 500 }}>
                                    {account.groupName}
                                  </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', margin: 0 }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </>
                )}

                {/* No accounts message */}
                {managerAccounts.length === 0 && subManagerAccounts.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      No accounts available
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Pending Requests Tab */}
            {activeTab === 1 && (
              <Box>
                {requests.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      No pending requests
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {requests.map((request) => (
                      <Box 
                        key={request.id}
                        sx={{ 
                          p: 2.5, 
                          border: request.status === 'ACCEPTED' ? '2px solid #4caf50' : 
                                  request.status === 'DENIED' ? '2px solid #f44336' : 
                                  '1px solid #e0e0e0',
                          backgroundColor: request.status === 'ACCEPTED' ? '#f1f8e9' : 
                                         request.status === 'DENIED' ? '#ffebee' : 
                                         '#fafafa',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: request.status === 'ACCEPTED' ? '#e8f5e8' : 
                                           request.status === 'DENIED' ? '#ffebee' : 
                                           '#f5f5f5',
                            transition: 'background-color 0.2s ease'
                          }
                        }}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            mb: 1, 
                            fontWeight: 500,
                            color: '#333',
                            fontSize: '0.9rem'
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 700 }}>
                            {request.entityName}
                          </Box> has invited you as a <Box component="span" sx={{ fontWeight: 700 }}>
                            {request.parentRole}
                          </Box> on his Prime Account
                        </Typography>
                        
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#666',
                            fontSize: '0.75rem',
                            display: 'block',
                            mb: 2
                          }}
                        >
                          Requested on: {new Date(request.createdAt).toLocaleDateString()}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                          <Button
                            variant={request.status === 'ACCEPTED' ? "contained" : "outlined"}
                            startIcon={<CheckCircleIcon sx={{ fontSize: '16px' }} />}
                            onClick={() => handleAccept(request.id)}
                            disabled={loading}
                            sx={{
                              bgcolor: request.status === 'ACCEPTED' ? '#4caf50' : 'transparent',
                              borderColor: request.status === 'ACCEPTED' ? '#4caf50' : '#1976d2',
                              color: request.status === 'ACCEPTED' ? 'white' : '#1976d2',
                              '&:hover': { 
                                bgcolor: request.status === 'ACCEPTED' ? '#45a049' : 'rgba(25, 118, 210, 0.04)',
                                borderColor: request.status === 'ACCEPTED' ? '#45a049' : '#1565c0'
                              },
                              minWidth: '80px',
                              height: '32px'
                            }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant={request.status === 'DENIED' ? "contained" : "outlined"}
                            startIcon={<CancelIcon sx={{ fontSize: '16px' }} />}
                            onClick={() => handleDecline(request.id)}
                            disabled={loading}
                            sx={{
                              bgcolor: request.status === 'DENIED' ? '#f44336' : 'transparent',
                              borderColor: request.status === 'DENIED' ? '#f44336' : '#333',
                              color: request.status === 'DENIED' ? 'white' : '#333',
                              '&:hover': {
                                bgcolor: request.status === 'DENIED' ? '#d32f2f' : 'rgba(0,0,0,0.04)',
                                borderColor: request.status === 'DENIED' ? '#d32f2f' : '#000'
                              },
                              minWidth: '80px',
                              height: '32px'
                            }}
                          >
                            Decline
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {/* Continue Button */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={
              (activeTab === 0 && !selectedAccount) || 
              (activeTab === 1 && markedRequests.length === 0) || 
              loading ||
              apiLoading
            }
            sx={{
              bgcolor: (activeTab === 0 && !selectedAccount) || 
                       (activeTab === 1 && markedRequests.length === 0) ? '#ccc' : '#1976d2',
              color: 'white',
              py: 1.2,
              px: 3,
              fontSize: '0.9rem',
              width: '100%',
              fontWeight: 500,
              '&:hover': {
                bgcolor: (activeTab === 0 && !selectedAccount) || 
                         (activeTab === 1 && markedRequests.length === 0) ? '#ccc' : '#1565c0'
              },
              '&:disabled': {
                bgcolor: '#ccc',
                color: '#999'
              }
            }}
          >
            {loading ? 'Processing...' : 'CONTINUE'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default AccountsComponent; 